<!-- src/lib/components/filters/QueryResultList.svelte -->
<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { onDestroy } from 'svelte';
  import Loading from '$lib/components/Loading.svelte';
  import NoResults from '$lib/components/NoResults.svelte';
  import EmptyStateClassic from '$lib/components/EmptyStateClassic.svelte';
  import { hydrate } from '$lib/stores/hydration';
  import EmptyStateNeighbor from '$lib/components/EmptyStateNeighbor.svelte';
  import VirtualList from '@sveltejs/svelte-virtual-list';
  import type { ComponentType, SvelteComponent } from 'svelte';

  /**
   * Which component draws one row.
   *
   * Given by the shell rather than chosen here: the public shell draws the
   * masked row, the app shell draws the signed-in one, and they carry different
   * data. Results renders what it is handed — a component that picked for
   * itself would be the shape that made the front screen and the industry
   * picker misbehave.
   *
   * Typed by what this component needs of a row — a result and whether its
   * similar-companies panel is open — rather than as `typeof ResultItem`. The
   * public row also dispatches `findSimilar` and the member row does not, so
   * naming one implementation as the type would make the other unassignable
   * for an event Results does not require of it.
   *
   * # Required, deliberately
   *
   * It defaulted to the public row, and the similar-companies screen rendered
   * `<Results />` and got it — a member signed into the member build, shown the
   * masked preview row, for as long as that screen has existed. A default is
   * only safe where every caller wants the same thing, and here the whole point
   * is that they do not. Without one the omission is a type error.
   */
  export let rowComponent: ComponentType<
    SvelteComponent<{ result: CompanyWithContacts; showSimilarPanel?: boolean }>
  >;
  import SortingPanel from '$lib/components/SortingPanel.svelte';
  import {
    hasSearched,
    queryResult,
    queryRunning,
    queryFilters,
    resultStats
  } from '$lib/stores/selection';
  import type { CompanyWithContacts } from '$lib/types/api';
  import { neighborSamples } from '$lib/stores/neighbors';
  import { applicationMode } from '$lib/stores/mode';
  import { derived } from 'svelte/store';
  import { submitFilters } from '$lib/api/filters';
  import { submitNeighborSamples } from '$lib/api/neighbors';
  import { sidebarOpen } from '$lib/stores/ui';
  import SimilarCompaniesPanel from '$lib/components/SimilarCompaniesPanel.svelte';
  import {
    mixedResults,
    filteredMixedResults,
    hasMixedSources,
    setOriginalResults
  } from '$lib/stores/mixedResults';

  // Check if any search has been performed

  // Track when we get new search results to update mixedResults.
  //
  // `appliedResult` is what stops this running in a loop. The condition below
  // reads `$mixedResults`, so the statement re-runs whenever that store
  // changes — including when a block of rows is fetched into it — and would
  // then reset the companies back to `queryResult`, which holds the skeletons
  // the index arrived as. Every hydrated row was wiped the instant it landed,
  // so the list stayed a page of placeholders and re-rendered forever.
  //
  // Comparing by identity is enough: `queryResult` is replaced wholesale by a
  // search, never mutated.
  let appliedResult: CompanyWithContacts[] | null = null;

  $: if (
    $queryResult &&
    $queryResult.length >= 0 &&
    !$queryRunning &&
    $queryResult !== appliedResult
  ) {
    // Update mixedResults from queryResult when:
    // 1. We don't have mixed sources (no similarity results)
    // 2. We have no similarity batches (all removed)
    // 3. We have no companies in mixedResults (state was cleared)
    if (
      !$hasMixedSources ||
      $mixedResults.similarityBatches.length === 0 ||
      $mixedResults.companies.length === 0
    ) {
      appliedResult = $queryResult;
      setOriginalResults($queryResult);
    }
  }

  // Sorting state
  let sortBy = 'quality';
  let sortOrder = 'desc';

  // Scroll state for indicator
  let virtualListElement;

  // Pagination state for infinite scroll
  let itemsPerPage = 100;
  let displayedItems = itemsPerPage;
  let isLoadingMore = false;

  // Virtual list configuration - disabled for now to fix infinite scroll
  let useVirtualList = false;

  // Similar companies panel state
  let showSimilarPanel = false;
  let similarSourceCompany = { id: '', name: '' };

  // Track when searches are performed
  $: if ($queryRunning) {
    hasSearched.set(true);
  }

  // Whether the server trimmed this response, and what it says really matched.
  // Only for a plain search: a mixed result set is assembled client-side from
  // several responses, so neither figure describes what is on screen.
  $: cappedResult = !$hasMixedSources && $resultStats?.capped === true;
  $: matchedTotal = $resultStats?.company_count as number | undefined;

  // Helper function to get numeric size value for sorting
  function getSizeValue(size: string | undefined) {
    if (!size) return 0;

    // Parse range format (e.g., "1-10", "11-50", "51-100")
    const rangeMatch = size.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (rangeMatch) {
      const min = parseInt(rangeMatch[1]);
      const max = parseInt(rangeMatch[2]);
      return (min + max) / 2; // Use average for sorting
    }

    // Handle "1000+" or ">1000" format
    const plusMatch = size.match(/(\d+)\+|>(\d+)/);
    if (plusMatch) {
      const num = parseInt(plusMatch[1] || plusMatch[2]);
      return num + 500; // Add arbitrary amount to ensure it sorts after ranges
    }

    // Handle single number
    const singleMatch = size.match(/\d+/);
    if (singleMatch) {
      return parseInt(singleMatch[0]);
    }

    return 0;
  }

  // Reset pagination when results change
  $: if ($filteredMixedResults) {
    displayedItems = itemsPerPage;
    isLoadingMore = false;
  }

  // Sort results based on current sorting criteria (use filtered mixed results)
  let sortedResults = [];
  $: sortedResults = (() => {
    const results = $filteredMixedResults || [];
    return [...results].sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'quality':
          compareValue = (a.quality || 0) - (b.quality || 0);
          break;
        case 'size': {
          const aSize = getSizeValue(a.size);
          const bSize = getSizeValue(b.size);
          compareValue = aSize - bSize;
          break;
        }
        case 'name':
          compareValue = (a.name || '').localeCompare(b.name || '');
          break;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
  })();

  // Calculate dynamic overscan based on list length

  function handleSortChange(event: CustomEvent<{ sortBy: string; sortOrder: string }>) {
    sortBy = event.detail.sortBy;
    sortOrder = event.detail.sortOrder;
  }

  // Show different states based on context
  const showEmptyState = derived(
    [
      queryRunning,
      mixedResults,
      filteredMixedResults,
      applicationMode,
      neighborSamples,
      hasSearched
    ],
    ([$queryRunning, $mixedResults, , $applicationMode, , $hasSearched]) => {
      if ($queryRunning) return false;
      // Check if we have results (but they might be filtered out)
      if ($mixedResults.companies && $mixedResults.companies.length > 0) {
        // If we have results but filtered is empty, don't show empty state
        return false;
      }

      // Show empty state if no search performed yet
      if (!$hasSearched) {
        if ($applicationMode === 'classic') return 'classic-empty';
        if ($applicationMode === 'neighbor') return 'neighbor-empty';
      }

      // Show no results if search was performed but no results
      if ($hasSearched && $mixedResults.companies && $mixedResults.companies.length === 0) {
        return 'no-results';
      }

      return false;
    }
  );

  // Filter change detection logic
  let previousFilters: string | null = null;
  let previousNeighborSamples: string | null = null;
  let showReloadOverlay = false;

  /**
   * Whether the list on screen is the answer to a search, empty or not.
   *
   * The overlay used to need rows, so a filter that had narrowed the search to
   * nothing could be unticked with no sign that "No results" was still its
   * answer: the pill was gone and the empty list stayed, which reads as a
   * filter that is applied without being selected. The search is not re-run on
   * its own — several filters often change at once, and each would be a query
   * — so the overlay is the cue, and it has to show over an empty answer too.
   */
  $: answered = $hasSearched && Array.isArray($mixedResults.companies);

  // Track filter changes when there is an answer on screen
  $: if (answered && !$queryRunning) {
    if ($applicationMode === 'classic' && previousFilters) {
      const filtersChanged = JSON.stringify($queryFilters) !== JSON.stringify(previousFilters);
      if (filtersChanged) {
        showReloadOverlay = true;
      }
    } else if ($applicationMode === 'neighbor' && (previousFilters || previousNeighborSamples)) {
      const filtersChanged = JSON.stringify($queryFilters) !== JSON.stringify(previousFilters);
      const samplesChanged =
        JSON.stringify($neighborSamples) !== JSON.stringify(previousNeighborSamples);
      if (filtersChanged || samplesChanged) {
        showReloadOverlay = true;
      }
    }
  }

  // Update previous filters when query completes successfully
  $: if (!$queryRunning && $queryResult && previousFilters === null) {
    // Initialize previousFilters after first successful query
    previousFilters = JSON.parse(JSON.stringify($queryFilters));
    previousNeighborSamples = JSON.parse(JSON.stringify($neighborSamples));
  }

  // Reset overlay when new query starts
  $: if ($queryRunning) {
    previousFilters = JSON.parse(JSON.stringify($queryFilters));
    previousNeighborSamples = JSON.parse(JSON.stringify($neighborSamples));
    showReloadOverlay = false;
  }

  const reloadResults = async () => {
    showReloadOverlay = false;
    // Close mobile sidebar when running search
    sidebarOpen.set(false);
    if ($applicationMode === 'classic') {
      submitFilters();
    } else if ($applicationMode === 'neighbor') {
      await submitNeighborSamples();
    }
  };

  // Handle find similar companies
  function handleFindSimilar(event: CustomEvent<{ id: string; name: string }>) {
    const { id, name } = event.detail;
    similarSourceCompany = { id, name };
    showSimilarPanel = true;
  }

  // Handle adding company from similar panel
  function handleAddToResults() {
    // The SimilarCompaniesPanel already adds to mixedResults store
    // which will automatically update the view through reactive statements
  }

  function handleCloseSimilar() {
    showSimilarPanel = false;
  }

  // Paginated results for display
  $: paginatedResults = sortedResults.slice(0, displayedItems);

  // Fetch the rows for what is on screen, and one page beyond it so scrolling
  // does not stutter.
  //
  // A search returns an index — every company, with what the list sorts by and
  // the identifiers an order is built from, but nothing a row displays — and
  // the rows arrive here. Re-running on every change is safe and cheap:
  // `hydrate` skips companies already fetched and those whose block is already
  // in flight, so the work is proportional to what has newly come into view.
  $: void hydrate(
    sortedResults.slice(0, displayedItems + itemsPerPage).map((company) => company.id)
  );

  // Load more items when scrolling near bottom
  function loadMoreItems() {
    if (isLoadingMore || displayedItems >= sortedResults.length) return;

    isLoadingMore = true;
    setTimeout(() => {
      displayedItems = Math.min(displayedItems + itemsPerPage, sortedResults.length);
      isLoadingMore = false;
    }, 100);
  }

  // Handle scroll manually to avoid reactive loops
  function handleScroll(event: Event) {
    const element = event.target as HTMLElement;
    const scrollPosition = element.scrollTop + element.clientHeight;
    const scrollHeight = element.scrollHeight;
    const distanceFromBottom = scrollHeight - scrollPosition;

    // Load more when near bottom (within 200px)
    if (distanceFromBottom < 200 && !isLoadingMore && displayedItems < sortedResults.length) {
      loadMoreItems();
    }
  }

  // Clean up on destroy
  onDestroy(() => {
    // Any cleanup if needed
  });
</script>

<div
  class="h-full {showSimilarPanel ? 'mr-96' : ''} relative"
  style="overflow: hidden; contain: layout;"
>
  <!--
    No background of its own. The shell paints the canvas; this painted
    gray-100 over it, which was invisible while the shell was also gray-100 and
    became a visible seam the moment the shell moved onto the token.
  -->
  <div class="flex h-full flex-col">
    <!--
      Rendered only when it has something to show. The container was
      unconditional and everything inside it was conditional, so with no
      results it drew an empty 16px band across the top of the content area —
      a strip with a different background and no controls in it.
    -->
    {#if $mixedResults.companies?.length && !$queryRunning}
      <div class="flex-shrink-0 px-4 pt-4 sm:px-6 lg:px-8">
        <!-- Sorting Panel -->
        <SortingPanel {sortBy} {sortOrder} on:sortChange={handleSortChange} />

        <!--
          "Add all to a list" stood here, in a row of its own under the sorting
          panel. It has moved to the top bar, into the slot the public build
          gives to ShareSelection — see +layout. It is the same act as that
          control claims to be, so it belongs in the same place rather than in
          a second one further down the page.
        -->

        <!-- Show filter message if results are filtered -->
        {#if $filteredMixedResults.length < $mixedResults.companies.length}
          <div class="mt-2 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
            {$_('status.showingFiltered', {
              values: {
                visible: $filteredMixedResults.length,
                total: $mixedResults.companies.length
              }
            })}
          </div>
        {/if}

        <!--
          The server caps a selection response, so a very broad search comes
          back trimmed. Saying so is not optional: the rows are what the order
          is built from, and a customer who asked for every Geschäftsführer
          would otherwise be shown 20,000 with nothing to suggest that 682,000
          more matched.
        -->
        {#if cappedResult}
          <div class="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {$_('status.showingCapped', {
              values: {
                visible: $mixedResults.companies.length.toLocaleString('de-CH'),
                total: (matchedTotal ?? 0).toLocaleString('de-CH')
              }
            })}
          </div>
        {/if}

        <!-- Show mixed sources indicator -->
        {#if $hasMixedSources}
          <div class="mt-2 rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-700">
            {$_('status.resultsIncludeSimilarity')}
          </div>
        {/if}
      </div>
    {/if}

    <div class="relative flex-1 overflow-hidden" style="max-height: 100%;">
      {#if $queryRunning}
        <div class="flex h-full items-center justify-center px-4 sm:px-6 lg:px-8">
          <Loading />
        </div>
      {:else if $showEmptyState === 'classic-empty'}
        <!--
          The guided screen is classic mode's front door, and it takes the
          whole area rather than sitting centred in it: it is a sequence of
          steps with a running total, not a placeholder.
        -->
        <!--
          Which front screen classic mode shows is the shell's question, and it
          is answered in +layout.svelte. What is left here is the one case the
          shell hands over: a classic selection that found nothing, with the
          guide switched off.
        -->
        <div class="flex h-full items-center justify-center px-4 sm:px-6 lg:px-8">
          <EmptyStateClassic />
        </div>
      {:else if $showEmptyState === 'neighbor-empty'}
        <div class="flex h-full items-center justify-center px-4 sm:px-6 lg:px-8">
          <EmptyStateNeighbor />
        </div>
      {:else if $showEmptyState === 'no-results'}
        <div class="flex h-full items-center justify-center px-4 sm:px-6 lg:px-8">
          <NoResults />
        </div>
      {:else if $filteredMixedResults?.length}
        <!-- Results List -->
        {#if useVirtualList}
          <!-- Virtual List for large datasets -->
          <div class="flex h-full flex-col px-4 py-2 pb-4 sm:px-6 lg:px-8">
            <div class="flex-1 overflow-auto">
              <VirtualList
                items={sortedResults}
                let:item
                itemHeight={80}
                bind:this={virtualListElement}
              >
                <div class="py-1">
                  <svelte:component
                    this={rowComponent}
                    result={item}
                    showSimilarPanel={showSimilarPanel && similarSourceCompany.id === item.id}
                    on:findSimilar={handleFindSimilar}
                  />
                </div>
              </VirtualList>
            </div>

            <!-- Status bar for virtual list -->
            <div
              class="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-2 text-center text-sm text-gray-500"
            >
              {$_('status.allResultsLoaded', { values: { count: sortedResults.length } })}
            </div>
          </div>
        {:else}
          <!-- Regular pagination for smaller datasets -->
          <div class="h-full overflow-auto px-4 py-2 pb-4 sm:px-6 lg:px-8" on:scroll={handleScroll}>
            {#each paginatedResults as item (item.id)}
              <div class="py-1">
                <svelte:component
                  this={rowComponent}
                  result={item}
                  showSimilarPanel={showSimilarPanel && similarSourceCompany.id === item.id}
                  on:findSimilar={handleFindSimilar}
                />
              </div>
            {/each}

            <!-- Load More Status -->
            {#if displayedItems < sortedResults.length}
              <div class="py-8 text-center">
                {#if isLoadingMore}
                  <div class="inline-flex items-center gap-2 text-gray-500">
                    <svg class="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span class="text-sm">{$_('status.loadingMore')}</span>
                  </div>
                {:else}
                  <div class="text-sm text-gray-500">
                    {$_('status.showingXofY', {
                      values: { visible: displayedItems, total: sortedResults.length }
                    })}
                  </div>
                {/if}
              </div>
            {:else if sortedResults.length > itemsPerPage}
              <div class="py-4 text-center text-sm text-gray-500">
                {$_('status.allResultsLoaded', { values: { count: sortedResults.length } })}
              </div>
            {/if}
          </div>
        {/if}
      {/if}
    </div>
  </div>

  <!-- Filter Changed Overlay - Covers entire Results component including sorting panel -->
  {#if showReloadOverlay && answered && ($applicationMode === 'classic' || $applicationMode === 'neighbor')}
    <div
      class="absolute inset-0 z-[60] flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm"
    >
      <div class="dialog w-full max-w-md md:max-w-lg lg:max-w-xl">
        <div class="flex items-center justify-between">
          <h3 class="dialog-title mb-0 flex items-center">
            <svg
              class="mr-2 h-5 w-5 text-accent-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {$_('dialogs.filtersChanged.title')}
          </h3>
          <button
            type="button"
            on:click={() => (showReloadOverlay = false)}
            class="close-button"
            title={$_('buttons.close')}
          >
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div class="dialog-content mt-6">
          <p class="text-gray-700">
            {$_('dialogs.filtersChanged.message')}
          </p>
        </div>

        <div class="dialog-actions justify-center">
          <button type="button" on:click={reloadResults} class="btn btn-primary">
            <svg class="mr-2 inline h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {$_('dialogs.filtersChanged.reloadResults')}
          </button>
        </div>
      </div>
    </div>
  {/if}
</div>

<!-- Similar Companies Panel -->
{#if showSimilarPanel}
  <SimilarCompaniesPanel
    sourceName={similarSourceCompany.name}
    sourceId={similarSourceCompany.id}
    isOpen={showSimilarPanel}
    on:close={handleCloseSimilar}
    on:addToResults={handleAddToResults}
  />
{/if}

<!-- AI Refinement Floating Button and Dialog - COMMENTED OUT -->
<!--
  {#if $mixedResults.companies?.length && !$queryRunning}
    <button
      class="group fixed bottom-28 right-6 z-30 flex items-center space-x-2 rounded-full bg-accent-600 px-5 py-3 font-semibold text-white shadow-lg transition-all hover:bg-accent-700 hover:shadow-xl"
      on:click={() => (overlayVisible = true)}
    >
      <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v18m9-9H3" />
      </svg>
      <span class="hidden sm:inline">{$_('filters.aiRefine.button')}</span>
      <div class="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center">
        <div class="h-2 w-2 animate-pulse rounded-full bg-yellow-400"></div>
      </div>
    </button>

    {#if overlayVisible}
      <div
        class="fixed -inset-4 z-[100] bg-black/20"
        on:click={() => (overlayVisible = false)}
      ></div>

      <div
        class="fixed bottom-28 right-6 z-[101] w-96 max-w-[calc(100vw-3rem)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
      >
        <div class="bg-accent-600 p-4 text-white">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <h3 class="text-sm font-bold">{$_('filters.aiRefine.title')}</h3>
                <p class="text-xs text-accent-100">{$_('filters.aiRefine.subtitle')}</p>
              </div>
            </div>
            <button
              type="button"
              on:click={() => (overlayVisible = false)}
              class="close-button !bg-white/20 hover:!bg-white/30"
              title={$_('buttons.close')}
            >
              <svg class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div class="space-y-4 p-4">
          <div>
            <label class="mb-2 block text-xs font-semibold text-gray-800">
              Describe what you're looking for
            </label>
            <textarea
              class="w-full resize-none rounded-lg border-2 border-gray-200 px-3 py-2 text-sm transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent-500"
              rows="3"
              placeholder={$_('form.placeholders.describeService')}
              bind:value={$embeddingQuery}
            ></textarea>
            <div class="mt-1 flex items-center text-xs text-gray-500">
              <svg class="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Be specific about industry, location, or requirements
            </div>
          </div>

          <div class="flex space-x-2">
            <button
              type="button"
              class="flex-1 rounded-lg border border-gray-200 !bg-none px-3 py-2 text-sm font-medium text-gray-700 !shadow-none transition-colors hover:bg-gray-50"
              on:click={() => (overlayVisible = false)}
            >
              Cancel
            </button>
            <button
              type="button"
              class="flex-1 rounded-lg bg-accent-600 px-3 py-2 text-sm font-medium text-white shadow-md transition-all hover:bg-accent-700 hover:shadow-lg"
              on:click={() => {
                overlayVisible = false;
                aiRefine();
              }}
            >
              <div class="flex items-center justify-center space-x-1">
                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                <span>{$_('filters.aiRefine.submit')}</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    {/if}
  {/if}
  -->
